import React from 'react';
import { 
  Form, 
  Input, 
  Radio, 
  Button, 
  Upload,
  Typography,
  Card,
  AutoComplete,
} from 'antd';
import { 
  PictureOutlined,
  PlusOutlined,
  DeleteOutlined,
  CloseOutlined
} from '@ant-design/icons';
import { adminWordApi } from '@/services/api';

const { Title } = Typography;

interface FlashCardWord {
  id: string;
  word: string;
  meaning: string;
  wordImage?: File;
  meaningImage?: File;
  wordImageUrl?: string;
  meaningImageUrl?: string;
}

interface FlashCardSectionProps {
  recordingEnabled: boolean;
  onRecordingEnabledChange: (enabled: boolean) => void;
  initialWords?: Array<{ id?: string; word: string; meaning: string; wordImageUrl?: string }>; // 초기 로드 데이터
  onWordImageUpload?: (id: string, url: string) => void;
  onMeaningImageUpload?: (id: string, url: string) => void;
  onWordImageFileChange?: (id: string, file: File) => void;
  onMeaningImageFileChange?: (id: string, file: File) => void;
  enableImageUpload?: boolean; // 에세이에서는 이미지 비활성화
  title?: string; // 유형별 타이틀
}

const FlashCardSection: React.FC<FlashCardSectionProps> = ({
  recordingEnabled,
  onRecordingEnabledChange,
  initialWords,
  onWordImageUpload,
  onMeaningImageUpload,
  enableImageUpload = true,
  title = '플래시 카드'
}) => {
  const form = Form.useFormInstance();
  const [words, setWords] = React.useState<FlashCardWord[]>([
    { id: '1', word: '', meaning: '' }
  ]);
  const nextWordIdRef = React.useRef<number>(2);
  const [options, setOptions] = React.useState<{ key: string; value: string; label: string; item: any }[]>([]);
  const searchTimer = React.useRef<number | undefined>(undefined);

  const syncNextWordId = React.useCallback((list: FlashCardWord[]) => {
    const maxId = list.reduce((max, word) => {
      const numericId = Number(word.id);
      if (Number.isFinite(numericId)) {
        return Math.max(max, numericId);
      }
      return max;
    }, 0);
    nextWordIdRef.current = Math.max(maxId + 1, 2);
  }, []);

  const allocateWordId = React.useCallback(() => {
    const id = String(nextWordIdRef.current);
    nextWordIdRef.current += 1;
    return id;
  }, []);

  // 초기 로드 데이터 반영
  React.useEffect(() => {
    if (initialWords && initialWords.length > 0) {
      const mapped: FlashCardWord[] = initialWords.map((w, idx) => ({
        id: String(w.id ?? idx + 1),
        word: w.word || '',
        meaning: w.meaning || '',
        wordImageUrl: w.wordImageUrl || undefined,
      }));
      setWords(mapped);
      // 폼 필드도 세팅
      const values: Record<string, any> = {};
      mapped.forEach(w => {
        values[`fc_word_${w.id}`] = w.word;
        values[`fc_meaning_${w.id}`] = w.meaning;
        if (w.wordImageUrl) values[`fc_wordImage_${w.id}`] = w.wordImageUrl;
      });
      form.setFieldsValue(values);
      syncNextWordId(mapped);
    } else {
      // 최소 1개 보장
      const initial = [{ id: '1', word: '', meaning: '' }];
      setWords(initial);
      form.setFieldsValue({ fc_word_1: '', fc_meaning_1: '', fc_wordImage_1: undefined });
      syncNextWordId(initial);
    }
  }, [initialWords, form, syncNextWordId]);

  const addWord = () => {
    const newId = allocateWordId();
    setWords(prev => {
      const next = [...prev, { id: newId, word: '', meaning: '' }];
      form.setFieldsValue({ [`fc_word_${newId}`]: '', [`fc_meaning_${newId}`]: '', [`fc_wordImage_${newId}`]: undefined });
      return next;
    });
  };

  const removeWord = (id: string) => {
    if (words.length > 1) {
      setWords(words.filter(word => word.id !== id));
      // 폼 필드 값도 제거하여 buildPayload에서 제외되도록 처리
      form.setFieldsValue({
        [`fc_word_${id}`]: undefined,
        [`fc_meaning_${id}`]: undefined,
        [`fc_wordImage_${id}`]: undefined,
        [`fc_wordImageFile_${id}`]: undefined,
      });
    }
  };

  // const updateWord = (id: string, field: 'word' | 'meaning', value: string) => {
  //   setWords(words.map(word => 
  //     word.id === id ? { ...word, [field]: value } : word
  //   ));
  // };

  const handleWordImageUpload = async (id: string, info: any) => {
    const candidate = (info?.file && (info.file as any).originFileObj) || info?.file;
    if (candidate) {
      const file = candidate as File;
      const previewUrl = URL.createObjectURL(file);
      setWords(prev => prev.map(w => (w.id === id ? { ...w, wordImage: file, wordImageUrl: previewUrl } : w)));
      // 저장 시 업로드를 위해 폼에 파일 보관
      form.setFieldValue(`fc_wordImageFile_${id}`, file);
    }
  };

  // const handleMeaningImageUpload = (id: string, info: any) => {
  //   // 미사용: 의미 이미지 업로드 기능 비활성화 상태
  // };

  const removeWordImage = (id: string) => {
    setWords(words.map(word => 
      word.id === id ? { 
        ...word, 
        wordImage: undefined,
        wordImageUrl: undefined
      } : word
    ));
    // 폼 내 이미지 관련 필드 초기화
    form.setFieldsValue({
      [`fc_wordImage_${id}`]: undefined,
      [`fc_wordImageFile_${id}`]: undefined,
    });
  };

  // const removeMeaningImage = (id: string) => {
  //   // 미사용: 의미 이미지 제거 기능 비활성화 상태
  // };

  const buildOptions = (list: any[]) => {
    const toKo = {
      gender: (v: string) => v === 'male' ? '남성' : v === 'female' ? '여성' : v,
      age: (v: string) => (
        v === 'child' ? '아동' : v === 'young' ? '청년' : v === 'adult' ? '성인' : v === 'elder' ? '노년' : v
      ),
      speed: (v: string) => v === 'slow' ? '느림' : v === 'normal' ? '보통' : v === 'fast' ? '빠름' : v,
      pitch: (v: string) => v === 'low' ? '낮음' : v === 'medium' ? '보통' : v === 'high' ? '높음' : v,
    } as const;
    return list.map((w: any) => {
      const g = toKo.gender(String(w.gender || ''));
      const a = toKo.age(String(w.age || ''));
      const s = toKo.speed(String(w.speed || ''));
      const p = toKo.pitch(String(w.pitch || ''));
      return {
        key: `${w.id}-${w.gender}-${w.age}-${w.speed}-${w.pitch}`,
        value: String(w.word || ''),
        label: `${w.word}(${g}|${a}|${s}|${p})`,
        item: w,
      };
    });
  };

  const searchWords = (query: string) => {
    if (searchTimer.current) {
      window.clearTimeout(searchTimer.current);
    }
    searchTimer.current = window.setTimeout(async () => {
      try {
        if (!query) {
          // 전체 로드: 여러 페이지 누적 수집 (최대 2000)
          const pageSize = 500;
          let page = 0;
          let all: any[] = [];
          // eslint-disable-next-line no-constant-condition
          while (true) {
            const res = await adminWordApi.list({ q: '', page, size: pageSize });
            const data = res?.data || res;
            const content = data?.content || data?.data?.content || [];
            all = all.concat(content);
            if (content.length < pageSize || all.length >= 2000) break;
            page += 1;
          }
          setOptions(buildOptions(all));
        } else {
          const res = await adminWordApi.list({ q: query, page: 0, size: 500 });
          const data = res?.data || res;
          const content = data?.content || data?.data?.content || [];
          const filtered = content.filter((w: any) => String(w.word || '').toLowerCase().startsWith(query.toLowerCase()));
          setOptions(buildOptions(filtered));
        }
      } catch (_) {
        setOptions([]);
      }
    }, 250);
  };

  return (
    <Card className='pb-5 border-b border-gray-200'>
      {/* 제목 */}
      <Title level={4} className='my-4'>{title}</Title>
        
      {/* 녹음 여부 */}
      <Form.Item
        label="녹음 여부"
        name="flashRecordingEnabled"
        rules={[
          { required: true, message: '녹음 여부를 선택해주세요' }
        ]}
      >
        <Radio.Group onChange={(e) => onRecordingEnabledChange(e.target.value)}>
          <Radio value={true}>녹음</Radio>
          <Radio value={false}>미녹음</Radio>
        </Radio.Group>
      </Form.Item>
        
      <section className='grid grid-cols-2 gap-4'>
        {words.map((word, index) => (
          <div key={word.id} className=''>
            <div className='flex gap-4 justify-between'>
              <p className='mb-4 text-lg font-bold'>단어 {index + 1}</p>
              {words.length > 1 && (
                <Button 
                  type='primary' 
                  danger 
                  ghost 
                  icon={<DeleteOutlined />} 
                  size='small'
                  onClick={() => removeWord(word.id)}
                >
                  단어 삭제
                </Button>
              )}
            </div>
            <div className='flex gap-4 w-full'>
              {/* 단어 그림 업로드 (에세이에서는 비활성화) */}
              {enableImageUpload && (
                <div className='w-[150px] h-[150px]'>
                  {word.wordImageUrl ? (
                    <div className='relative' style={{ width: '150px', height: '150px' }}>
                      <img 
                        src={word.wordImageUrl} 
                        alt="단어 이미지" 
                        className='object-cover rounded-lg border border-gray-200'
                        style={{ width: '150px', height: '150px' }}
                      />
                      <Button
                        type="primary"
                        shape="circle"
                        size="small"
                        icon={<CloseOutlined />}
                        style={{
                          position: 'absolute',
                          top: '-8px',
                          right: '-8px',
                          backgroundColor: '#ef4444',
                          borderColor: '#ef4444',
                          zIndex: 10
                        }}
                        onClick={() => removeWordImage(word.id)}
                      />
                    </div>
                  ) : (
                    <Upload
                      name="wordImage"
                      listType="picture-card"
                      className="word-image-uploader"
                      showUploadList={false}
                      beforeUpload={() => false}
                      accept="image/*"
                      onChange={(info) => handleWordImageUpload(word.id, info)}
                      style={{ width: '150px', height: '150px' }}
                    >
                      <div className='flex flex-col justify-center items-center rounded-lg' style={{ width: '150px', height: '150px' }}>
                        <PictureOutlined className='text-2xl text-gray-400' />
                        <div className='mt-2 text-gray-400'>그림 업로드</div>
                      </div>
                    </Upload>
                  )}
                </div>
              )}
                        
              <div className='flex flex-col w-full'>
                {/* 단어 입력 (자동완성) */}
                <Form.Item
                  name={`fc_word_${word.id}`}
                  rules={[
                    { required: true, message: '영어 단어를 입력해주세요' }
                  ]}
                >
                  <AutoComplete
                    placeholder="영어 단어를 입력하세요"
                    size="large"
                    onFocus={() => searchWords('')}
                    onSearch={searchWords}
                    onSelect={(val: string, option: any) => {
                      let sel = option?.item as any | undefined;
                      if (!sel) {
                        const byKey = options.find(o => (o as any).key === option?.key);
                        sel = byKey?.item;
                      }
                      if (!sel) {
                        const byVal = options.find(o => o.value === val);
                        sel = byVal?.item;
                      }
                      if (sel) {
                        form.setFieldsValue({
                          [`fc_word_${word.id}`]: sel.word ?? val,
                          [`fc_meaning_${word.id}`]: sel.meaning ?? ''
                        });
                        // 단어 선택 시 참조 ID 저장 (unit_activity_item.ref_id 용)
                        if (sel.id != null) {
                          form.setFieldValue(`fc_wordId_${word.id}`, sel.id);
                        }
                      } else {
                        form.setFieldsValue({ [`fc_word_${word.id}`]: val });
                      }
                    }}
                    options={options}
                    filterOption={false}
                  />
                </Form.Item>
                
                {/* 해석 입력 */}
                <Form.Item
                  name={`fc_meaning_${word.id}`}
                  rules={[
                    { required: true, message: '한글 해석을 입력해주세요' }
                  ]}
                >
                  <Input
                    placeholder="한글 해석을 입력하세요"
                    size="large"
                  />
                </Form.Item>
              </div>
            </div>
          </div>
        ))}
      </section>
      <Button 
        type='primary' 
        icon={<PlusOutlined />} 
        size='large'
        onClick={addWord}
        className='mt-4'
      >
        단어 추가
      </Button>
    </Card>
  );
};

export default FlashCardSection; 